import React, { Fragment, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import SEO from "../../components/seo";
import LayoutOne from "../../layouts/LayoutOne";
import NewsletterBar from "../../components/newdesign/NewsletterBar";
import { useGetOrderByIdQuery } from "../../store/slices/orderSlice";
import { getDiscountPrice } from "../../helpers/product";
import { formatMoney } from "../../components/cart-redesign/pricing";
import TrackForm from "../../components/track-redesign/TrackForm";
import OrderHeader from "../../components/track-redesign/OrderHeader";
import StatusStepper from "../../components/track-redesign/StatusStepper";
import ActivityLog from "../../components/track-redesign/ActivityLog";
import ShipmentItems from "../../components/track-redesign/ShipmentItems";
import InfoPanel, { InfoRow } from "../../components/track-redesign/InfoPanel";
import StateMessage from "../../components/track-redesign/StateMessage";

const SUPPORT_EMAIL = "info@cocgreeninternational.co.uk";
const SUPPORT_PHONE = "+44(0)20 3371 178";

function OrderStatus() {
  const [submittedOrderId, setSubmittedOrderId] = useState(null);

  // Fetch order data using the Redux query
  const {
    data: order,
    isFetching,
    isError,
    error,
  } = useGetOrderByIdQuery(submittedOrderId, {
    skip: !submittedOrderId, // Skip the query if no order ID is submitted
  });

  const currency = useSelector((state) => state.currency);
  const { products } = useSelector((state) => state.product);

  /*
   * The order endpoint populates only each item's `name`, so the image and
   * unit price are looked up in the catalogue already held in the store —
   * the same approach the order confirmation page takes. Items no longer in
   * the catalogue still render with their name and quantity.
   */
  const lines = useMemo(() => {
    if (!order?.items) return [];
    const rate = currency.currencyRate || 1;

    return order.items.map((item) => {
      const productId = item.product?._id || item.product;
      const full = products.find((candidate) => candidate._id === productId);
      const base = full?.price?.basePrice;
      const discounted =
        base != null ? getDiscountPrice(base, full.discount) : null;
      const unit =
        base == null ? null : +((discounted ?? base) * rate).toFixed(2);

      return {
        key: productId || item.product?.name,
        name: item.product?.name || full?.name || "Product",
        quantity: item.quantity,
        image: full?.mainImage,
        unit,
        lineTotal: unit == null ? null : +(unit * item.quantity).toFixed(2),
      };
    });
  }, [order, products, currency.currencyRate]);

  const formatPrice = (value) => formatMoney(currency, value);

  const orderNumber =
    order?.customFullOrderId ||
    (order?.customOrderId ? `#${order.customOrderId}` : order?._id);

  const placedAt = order?.createdAt ? new Date(order.createdAt) : null;

  const deliveryName =
    order?.deliveryInfo?.DeliveryFullName ||
    [order?.billingInfo?.firstName, order?.billingInfo?.lastName]
      .filter(Boolean)
      .join(" ");

  const deliveryAddress = order
    ? [
        order.deliveryInfo?.DeliveryStreetAddress ||
          order.billingInfo?.streetAddress,
        order.deliveryInfo?.DeliveryApartment || order.billingInfo?.apartment,
        order.deliveryInfo?.DeliveryCity || order.billingInfo?.city,
        order.deliveryInfo?.DeliveryState || order.billingInfo?.state,
        order.deliveryInfo?.DeliveryPostalCode || order.billingInfo?.postalCode,
        order.billingInfo?.country,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  const deliveryPhone =
    order?.deliveryInfo?.DeliveryPhone || order?.billingInfo?.phone;
  const deliveryEmail =
    order?.deliveryInfo?.DeliveryEmail || order?.billingInfo?.email;

  const results = () => {
    if (!submittedOrderId) {
      return (
        <StateMessage
          title="Nothing to track yet"
          text="Enter an order ID above and we'll show its live shipment progress, delivery details and items."
        />
      );
    }

    if (isFetching) {
      return (
        <StateMessage
          tone="loading"
          title="Looking up your order…"
          text="This only takes a moment."
        />
      );
    }

    if (isError) {
      return (
        <StateMessage
          tone="error"
          title="We couldn't find that order"
          text={
            error?.data?.message ||
            "Check the order ID from your confirmation email and try again."
          }
        >
          <div className="c4trk__actions">
            <Link
              className="c4trk__btn c4trk__btn--outline"
              to={process.env.PUBLIC_URL + "/contact"}
            >
              Contact Support
            </Link>
          </div>
        </StateMessage>
      );
    }

    if (!order) return null;

    return (
      <div className="c4trk__results">
        <OrderHeader
          orderNumber={orderNumber}
          placedAt={placedAt}
          itemCount={order.items?.length || 0}
          total={
            order.overallTotal != null ? formatPrice(order.overallTotal) : null
          }
          shippingMethod={order.shippingMethod}
          status={order.status}
        />

        <StatusStepper status={order.status} />

        <div className="c4trk__card">
          <ActivityLog
            status={order.status}
            createdAt={order.createdAt}
            trackId={order.trackId}
            shippingMethod={order.shippingMethod}
          />
        </div>

        <div className="c4trk__panels">
          <InfoPanel title="Delivery Address">
            <InfoRow label="Name" value={deliveryName} />
            <InfoRow label="Phone" value={deliveryPhone} />
            <InfoRow label="Email" value={deliveryEmail} />
            <InfoRow label="Address" value={deliveryAddress} />
            <InfoRow
              label="Notes"
              value={order.deliveryInfo?.DeliveryOrderNotes}
            />
          </InfoPanel>

          <InfoPanel title="Carrier Information">
            <InfoRow label="Service" value={order.shippingMethod || "Standard"} />
            <InfoRow
              label="Tracking"
              value={order.trackId || "Pending dispatch"}
            />
            <InfoRow label="Reference" value={orderNumber} />
            <InfoRow
              label="Shipping"
              value={
                order.totalDeliveryCost > 0
                  ? formatPrice(order.totalDeliveryCost)
                  : "Free"
              }
            />
          </InfoPanel>

          <InfoPanel
            title="Contact & Support"
            footer={
              <Link
                className="c4trk__btn c4trk__btn--outline c4trk__btn--block"
                to={process.env.PUBLIC_URL + "/contact"}
              >
                Contact Support
              </Link>
            }
          >
            <InfoRow
              label="Email"
              value={<a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>}
            />
            <InfoRow
              label="Phone"
              value={
                <a href={`tel:${SUPPORT_PHONE.replace(/[^\d+]/g, "")}`}>
                  {SUPPORT_PHONE}
                </a>
              }
            />
            <InfoRow label="Hours" value="Mon–Fri, 9am – 6pm" />
          </InfoPanel>
        </div>

        {lines.length ? (
          <ShipmentItems lines={lines} formatPrice={formatPrice} />
        ) : null}

        <div className="c4trk__footer-actions">
          <Link
            className="c4trk__btn c4trk__btn--primary"
            to={process.env.PUBLIC_URL + "/shop-grid-standard"}
          >
            Continue Shopping
          </Link>
          <Link
            className="c4trk__btn c4trk__btn--outline"
            to={process.env.PUBLIC_URL + "/my-account"}
          >
            View Order Details
          </Link>
          <Link
            className="c4trk__btn c4trk__btn--outline"
            to={process.env.PUBLIC_URL + "/refund"}
          >
            Request Return
          </Link>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <SEO
        titleTemplate="Track Your Order"
        description="Track the progress of your Cey4Hub order."
      />

      <LayoutOne headerTop="visible">
        <main className="c4trk">
          <div className="c4wide">
            <nav className="c4trk__crumbs" aria-label="Breadcrumb">
              <Link to={process.env.PUBLIC_URL + "/"}>Home</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Track your order</span>
            </nav>

            <TrackForm
              initialValue={submittedOrderId || ""}
              isLoading={isFetching}
              onSubmit={setSubmittedOrderId}
            />

            {results()}
          </div>
        </main>

        <NewsletterBar />
      </LayoutOne>
    </Fragment>
  );
}

export default OrderStatus;
